import {
    Availability,
    AvailabilityType
} from "crqlar-react-common/dist/models/availability/availability.model";
import {
    AvailabilityException
} from "crqlar-react-common/dist/models/availability/availabilityException.model";

export interface RRuleOccurrence {
    start: Date;
    end: Date;
    isClosed: boolean;
    capacity?: number;
}

export const computeRRule = (
    availability: Availability,
    exceptions: AvailabilityException[]
): {
    occurrences: RRuleOccurrence[];
    firstDate?: Date;
    lastDate?: Date;
} => {
    const occurrences: RRuleOccurrence[] = [];

    // no times -> nothing to do
    if (!availability.startTime || !availability.endTime) {
        return {occurrences, firstDate: undefined, lastDate: undefined};
    }

    const baseStart = availability.startTime;
    const baseEnd = availability.endTime;
    const durationMs = baseEnd.getTime() - baseStart.getTime();

    const parseRRuleDate = (value: string): Date => {
        const cleaned = value.replace(/Z$/, "");
        const year = parseInt(cleaned.slice(0, 4), 10);
        const month = parseInt(cleaned.slice(4, 6), 10) - 1;
        const day = parseInt(cleaned.slice(6, 8), 10);

        if (cleaned.length > 8 && cleaned[8] === "T") {
            const hour = parseInt(cleaned.slice(9, 11), 10);
            const minute = parseInt(cleaned.slice(11, 13), 10);
            const second = parseInt(cleaned.slice(13, 15), 10);
            return new Date(year, month, day, hour, minute, second);
        }

        return new Date(year, month, day);
    };

    // no rrule -> single occurrence from availability
    if (!availability.rrule) {
        const single: RRuleOccurrence = {
            start: baseStart,
            end: baseEnd,
            isClosed: availability.type === AvailabilityType.CLOSED,
            capacity: availability.capacityOverride,
        };

        occurrences.push(single);

        return {
            occurrences,
            firstDate: single.start,
            lastDate: single.end,
        };
    }

    const rrule = availability.rrule.toUpperCase();

    // DTSTART
    const dtstartMatch = rrule.match(/DTSTART[;:]([^\r\n;]*)/);
    let dtstart: Date;
    if (dtstartMatch && dtstartMatch[1]) {
        dtstart = parseRRuleDate(dtstartMatch[1]);
    } else {
        dtstart = new Date(baseStart);
    }

    // UNTIL
    const untilMatch = rrule.match(/UNTIL=([^\r\n;]*)/);
    let until: Date | null = null;
    if (untilMatch && untilMatch[1]) {
        until = parseRRuleDate(untilMatch[1]);
    }

    // COUNT
    const countMatch = rrule.match(/COUNT=(\d+)/);
    const count = countMatch && countMatch[1] ? parseInt(countMatch[1], 10) : null;

    // FREQ
    const frequencyMatch = rrule.match(/FREQ=(DAILY|WEEKLY|MONTHLY|YEARLY)/);
    const frequency = frequencyMatch && frequencyMatch[1]
        ? (frequencyMatch[1] as "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY")
        : null;

    // INTERVAL
    const intervalMatch = rrule.match(/INTERVAL=(\d+)/);
    const interval = intervalMatch && intervalMatch[1] ? parseInt(intervalMatch[1], 10) : 1;

    // BYDAY
    const bydayMatch = rrule.match(/BYDAY=([^\r\n;]*)/);
    const bydays = bydayMatch && bydayMatch[1]
        ? bydayMatch[1].split(',').map(day => day.trim())
        : null;

    // Helper to convert BYDAY to JS day numbers (0=Sunday, 1=Monday, etc.)
    const dayMap: Record<string, number> = {
        'SU': 0, 'MO': 1, 'TU': 2, 'WE': 3, 'TH': 4, 'FR': 5, 'SA': 6
    };

    const maxCount = count || 100;
    const rawOccurrences: Date[] = [];

    if (!frequency) {
        rawOccurrences.push(new Date(dtstart));
    } else {
        for (let index = 0; index < maxCount; index++) {
            if (frequency === "WEEKLY" && bydays) {
                // For WEEKLY with BYDAY, we need to generate occurrences for each specified day
                const weekStart = new Date(dtstart);
                weekStart.setDate(weekStart.getDate() + index * interval * 7);

                // Get the Sunday of this week
                const currentDayOfWeek = weekStart.getDay();
                const sundayOfWeek = new Date(weekStart);
                sundayOfWeek.setDate(sundayOfWeek.getDate() - currentDayOfWeek);

                for (const byday of bydays) {
                    const targetDay = dayMap[byday];
                    if (targetDay !== undefined) {
                        const occurrence = new Date(sundayOfWeek);
                        occurrence.setDate(occurrence.getDate() + targetDay);

                        // Copy time from dtstart
                        occurrence.setHours(dtstart.getHours());
                        occurrence.setMinutes(dtstart.getMinutes());
                        occurrence.setSeconds(dtstart.getSeconds());
                        occurrence.setMilliseconds(dtstart.getMilliseconds());

                        // Only include if on or after dtstart
                        if (occurrence >= dtstart) {
                            if (until && occurrence > until) {
                                continue;
                            }
                            rawOccurrences.push(occurrence);
                        }
                    }
                }

                // Check if we've passed the until date for this week
                if (until && weekStart > until) {
                    break;
                }
            } else {
                // Original logic for non-BYDAY cases
                const occurrence = new Date(dtstart);

                if (frequency === "DAILY") {
                    occurrence.setDate(occurrence.getDate() + index * interval);
                } else if (frequency === "WEEKLY") {
                    occurrence.setDate(occurrence.getDate() + index * interval * 7);
                } else if (frequency === "MONTHLY") {
                    occurrence.setMonth(occurrence.getMonth() + index * interval);
                } else if (frequency === "YEARLY") {
                    occurrence.setFullYear(occurrence.getFullYear() + index * interval);
                }

                if (until && occurrence > until) {
                    break;
                }

                rawOccurrences.push(occurrence);
            }
        }
    }

    for (const occurrence of rawOccurrences) {
        const exception = exceptions.find(
            (ex) =>
                ex.originalStartTime &&
                ex.originalStartTime.getTime() === occurrence.getTime()
        );

        if (exception?.hidden || exception?.deleted) {
            continue;
        }

        const effectiveType = exception?.type ?? availability.type;
        const isClosedForOccurrence = effectiveType === AvailabilityType.CLOSED;

        const start = exception?.newStartTime || occurrence;
        const end = exception?.newEndTime || new Date(start.getTime() + durationMs);
        const capacity = exception?.capacityOverride ?? availability.capacityOverride;

        occurrences.push({
            start,
            end,
            isClosed: isClosedForOccurrence,
            capacity,
        });
    }

    if (occurrences.length === 0) {
        occurrences.push({
            start: baseStart,
            end: baseEnd,
            isClosed: availability.type === AvailabilityType.CLOSED,
            capacity: availability.capacityOverride,
        });
    }

    const firstDate = occurrences.length ? occurrences[0].start : undefined;
    const lastDate = occurrences.length ? occurrences[occurrences.length - 1].end : undefined;

    return {
        occurrences,
        firstDate,
        lastDate,
    };
};